import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Lock, ShieldCheck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { Brand, PortalLabel, PORTAL_THEMES } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const TERMS_OPTIONS = ["Due On Receipt", "Net 30", "Net 45", "Net 60"];

const QUESTIONS = [
  ["phone", "Your phone number", "e.g. 555-010-2020"],
  ["account_duration", "How long has this customer held an active account with you?", "e.g. 3 years, 4 months"],
  ["terms_extended", "What terms were extended?", "", TERMS_OPTIONS],
  ["highest_balance", "Highest balance granted", "e.g. $45,000"],
  ["current_balance", "Current outstanding balance", "e.g. $12,500"],
  ["past_due", "Any past-due amounts?", "e.g. None / $1,200 at 30 days"],
];

export default function ReferenceForm() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const magicKey = searchParams.get("k") || "";
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [done, setDone] = useState(false);
  const [magicFailed, setMagicFailed] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({
    phone: "",
    account_duration: "",
    terms_extended: "",
    highest_balance: "",
    current_balance: "",
    past_due: "",
    registry_opt_in: "no",
    comments: "",
  });

  useEffect(() => {
    api
      .get(`/public/reference/${token}`)
      .then(({ data }) => setMeta(data))
      .catch((e) => setError(errMsg(e)));
  }, [token]);

  useEffect(() => {
    if (!magicKey || unlocked || done) return;
    setBusy(true);
    api
      .post(`/public/reference/${token}/verify`, { magic_key: magicKey })
      .then(({ data }) => {
        setMeta((m) => ({ ...m, ...data }));
        if (data.trade_doc) setTradeDoc(data.trade_doc);
        if (data.status === "completed") setDone(true);
        else setUnlocked(true);
      })
      .catch(() => setMagicFailed(true))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, magicKey]);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/public/reference/${token}/verify`, { password });
      setMeta((m) => ({ ...m, ...data }));
      if (data.trade_doc) setTradeDoc(data.trade_doc);
      if (data.status === "completed") setDone(true);
      else setUnlocked(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const [uploading, setUploading] = useState(false);
  const [tradeDoc, setTradeDoc] = useState(null);

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("password", password);
      body.append("magic_key", magicKey);
      const { data } = await api.post(`/public/reference/${token}/upload`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTradeDoc(data.filename);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post(`/public/reference/${token}/submit`, {
        password,
        magic_key: magicKey,
        ...answers,
      });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className={`border-b border-black/10 ${PORTAL_THEMES.teal.labelBar}`}>
        <div className="mx-auto flex h-16 max-w-3xl items-center px-5">
          <PortalLabel label="Trade Reference Portal" theme={PORTAL_THEMES.teal} />
        </div>
      </header>

      <div className={`border-b border-black/10 ${PORTAL_THEMES.teal.band}`} data-testid="title-band">
        <div className="mx-auto max-w-3xl px-5 py-10">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-200">
            Trade Reference Portal
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">
            Reference Information
          </h1>
          <p className="mt-3 max-w-2xl text-base text-teal-50 md:text-lg" data-testid="role-line">
            Respond to trade reference requests and manage your registry profile.
          </p>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-5 py-12 lg:py-16">        {!meta && error && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 p-8 text-center"
            data-testid="reference-invalid"
          >
            <p className="font-display text-lg font-bold text-red-800">Link not valid</p>
            <p className="mt-2 text-sm text-red-700">{error}</p>
          </div>
        )}

        {meta && done && (
          <div
            className="ct-rise rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm"
            data-testid="reference-submitted"
          >
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
            <h1 className="mt-6 font-display text-3xl font-black tracking-tight text-slate-900">
              Thank you
            </h1>
            <p className="mt-3 text-base text-slate-600">
              Your credit reference for{" "}
              <strong className="text-slate-900">{meta.customer_company}</strong> has been recorded
              and shared securely with the requesting company.
            </p>
          </div>
        )}

        {meta && !done && !unlocked && magicKey && !magicFailed && (
          <div
            className="ct-rise mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm"
            data-testid="magic-link-loading"
          >
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-teal-700" />
            <p className="mt-5 text-sm text-slate-600">Opening your reference form…</p>
          </div>
        )}

        {meta && !done && !unlocked && (!magicKey || magicFailed) && (
          <div className="ct-rise mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            <Lock className="h-6 w-6 text-blue-600" />
            <h1 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-slate-900">
              Enter your access code
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {magicFailed
                ? "Your one-tap link has expired or has already been used. Enter the access code from the Vendor/Supplier that listed you, or ask them to resend the request."
                : null}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              <strong className="text-slate-900">{meta.customer_company}</strong> has listed{" "}
              {meta.reference_company} as a trade reference. Enter the access password you were given
              to continue.
            </p>
            <form onSubmit={verify} className="mt-7 space-y-5" data-testid="reference-gate-form">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Access password
                </Label>
                <PasswordInput
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  testId="reference-password-input"
                  className="mt-2 font-mono"
                />
              </div>
              {error && (
                <div
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                  data-testid="reference-gate-error"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={busy}
                data-testid="reference-unlock-button"
                className="h-11 w-full rounded-md bg-blue-600 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-[0.99]"
              >
                {busy ? "Verifying…" : "Unlock form"}
              </Button>
            </form>
          </div>
        )}

        {meta && !done && unlocked && (
          <form onSubmit={submit} className="ct-rise space-y-6" data-testid="reference-info-form">
            <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">
                <ShieldCheck className="h-3.5 w-3.5" /> Verified reference request
              </div>
              <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-slate-900">
                Your credit experience
              </h2>
              <p className="mt-3 text-base text-slate-600">
                Please provide your credit experience with{" "}
                <strong className="text-slate-900">{meta.customer_company}</strong>.
              </p>
              <p className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-600"
                data-testid="reference-registry-notice"
              >
                Would you like to be listed in our Reference registry? If so, this would be
                considered &ldquo;Generic Trade Reference&rdquo; information and would be given to
                requesting companies.
              </p>
              <div className="mt-4 flex flex-wrap gap-3" data-testid="registry-opt-in-choice">
                {[
                  ["yes", "Yes, list us"],
                  ["no", "No, thank you"],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setAnswers({ ...answers, registry_opt_in: val })}
                    data-testid={`registry-opt-in-${val}`}
                    className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
                      answers.registry_opt_in === val
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div
              className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm"
              data-testid="reference-upload-card"
            >
              <h3 className="font-display text-lg font-extrabold tracking-tight text-slate-900">
                Already have a trade reference letter?
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Upload your standard trade information as a PDF and you can skip the questions
                below. We attach it to the credit application packet exactly as you sent it.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <label
                  className="cursor-pointer rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:bg-slate-100"
                  data-testid="reference-upload-label"
                >
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    data-testid="reference-upload-input"
                    onChange={(e) => uploadDoc(e.target.files?.[0])}
                  />
                  {uploading ? "Uploading…" : tradeDoc ? "Replace PDF" : "Choose a PDF"}
                </label>
                {tradeDoc && (
                  <span
                    className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"
                    data-testid="reference-upload-filename"
                  >
                    {tradeDoc} attached
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-5 rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
              {meta.registry_prefill && (
                <div
                  className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-teal-200 bg-teal-50/60 p-4"
                  data-testid="registry-prefill-banner"
                >
                  <div>
                    <div className="font-display text-base font-bold text-[#1f4e5a]">
                      We have your registry profile on file
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      Fill this form with your saved answers, then adjust anything that has changed.
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() =>
                      setAnswers((a) => ({ ...a, ...meta.registry_prefill, registry_opt_in: "yes" }))
                    }
                    data-testid="use-registry-answers"
                    className="rounded-full bg-[#1f4e5a] px-5 font-semibold transition-colors duration-200 hover:bg-[#173c46] active:scale-95"
                  >
                    Use my saved answers
                  </Button>
                </div>
              )}
              {QUESTIONS.map(([key, label, ph, options]) => (
                <div key={key}>
                  <Label className="text-sm font-semibold text-slate-800">{label}</Label>
                  {options ? (
                    <select
                      value={answers[key]}
                      onChange={(e) => setAnswers({ ...answers, [key]: e.target.value })}
                      data-testid={`answer-${key}-select`}
                      className="mt-2 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 transition-colors duration-200 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                    >
                      <option value="">Select terms…</option>
                      {options.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      type={key === "phone" ? "tel" : "text"}
                      value={answers[key]}
                      placeholder={ph}
                      onChange={(e) =>
                        setAnswers({
                          ...answers,
                          [key]: key === "phone" ? formatPhone(e.target.value) : e.target.value,
                        })
                      }
                      data-testid={`answer-${key}-input`}
                      className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
                    />
                  )}
                </div>
              ))}
              <div>
                <Label className="text-sm font-semibold text-slate-800">
                  Additional comments (optional)
                </Label>
                <Textarea
                  value={answers.comments}
                  onChange={(e) => setAnswers({ ...answers, comments: e.target.value })}
                  data-testid="answer-comments-input"
                  className="mt-2 border-slate-300 focus-visible:ring-blue-600"
                  rows={3}
                />
              </div>
              {error && (
                <div
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                  data-testid="reference-submit-error"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={busy}
                data-testid="reference-submit-button"
                className="h-11 w-full rounded-md bg-blue-600 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-[0.99]"
              >
                {busy ? "Submitting…" : "Submit reference information"}
              </Button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
